// Stat Tracker v2 ("My stats"). Reads come from /api/st-v2 (gated on the server by STAT_V2_<ENV>).
// Stat values are SAVED through the same /api/st1 route every other Stat Tracker page uses, so the old
// pages and v2 always show the same numbers.
import { useEffect, useState } from 'react';
import httpHandler from '../../helpers/httpHandler';
import { putRequest } from '../../helpers/httpHandler2';
import { getMemberTeams, recordStat } from '../statTracker1/newInput/api';
import { MemberTeam } from '../statTracker1/newInput/types';

export type Side = 'buyer' | 'seller' | 'both';
export type Timeframe = 'day' | 'week' | 'month';
export type Source = 'crm' | 'typed';

export interface V2Team { teamId: number; name: string; leaderId: number; isLeader: boolean }
export interface V2Field { id: number; name: string; categoryId: number | null; category: string | null; side: Side }
export interface V2Target { id: number; name: string; low: number; medium: number; high: number; timeframe: Timeframe; visible: boolean; fieldIds: number[] }
export interface V2Value { day: string; fieldId: number; value: number; source: Source }
export interface V2Day { day: string; level: number | null; note: string | null }
export interface V2Settings { celebrate: boolean; shareCoach: boolean; tile: string[] }
export interface V2Bootstrap {
  teams: V2Team[];
  team: V2Team | null;
  fields?: V2Field[];
  targets?: V2Target[];
  values?: V2Value[];
  days?: V2Day[];
  crm?: { provider: string; status: string; lastFilled: string | null } | null;
  settings?: V2Settings;
}

const get = <T>(path: string, params?: object): Promise<T> => httpHandler.get(path, params) as Promise<T>;
const post = <T>(path: string, body: object): Promise<T> => httpHandler.post(path, body) as Promise<T>;

export const getFeatures = () => get<{ memberTools?: boolean; statV2?: boolean }>('/api/account/features');
export const bootstrap = (teamId: number | null, from: string, to: string) =>
  get<V2Bootstrap>('/api/st-v2/bootstrap', teamId ? { teamId, from, to } : { from, to });
export const saveDay = (teamId: number, day: string, patch: { level?: number | null; note?: string | null }) =>
  post<V2Day>('/api/st-v2/day', { teamId, day, ...patch });
export const saveTargets = (teamId: number, targets: { fieldId: number; low: number; medium: number; high: number; timeframe: Timeframe }[]) =>
  post<{ created: number; updated: number; targets: V2Target[] }>('/api/st-v2/targets', { teamId, targets });
export const saveSettings = (patch: Partial<V2Settings>) => post<V2Settings>('/api/st-v2/settings', patch);

/** The old routes need the legacy team row (it carries the leader for the squad lookup). */
let teamRows: Promise<MemberTeam[]> | null = null;
export const legacyTeam = async (teamId: number): Promise<MemberTeam | null> => {
  if (!teamRows) teamRows = getMemberTeams().catch((e) => { teamRows = null; throw e; });
  return (await teamRows).find((t) => Number(t.team_id) === teamId) || null;
};
/** Save the new ABSOLUTE total for a day (not a +1), exactly like the other Stat Tracker pages. */
export const saveValue = async (teamId: number, fieldId: number, value: number, day: string) => {
  const team = await legacyTeam(teamId);
  if (!team) throw new Error('That team could not be found. Reload the page.');
  return recordStat(team, fieldId, value, day);
};

/* ---- coaching board tile ---- */
export interface TileStat { fieldId: number; name: string; today: number; week: number; lastWeek: number; month: number; target: { low: number; medium: number; high: number; timeframe: Timeframe } | null; pace: { goalWeek: number; status: 'on_pace' | 'slightly_behind' | 'behind' } | null }
export interface TileData {
  widgetId: number;
  isClient: boolean;
  client: { id: number; name: string };
  crm: { provider: string; name: string; status: string; autoStats: boolean } | null;
  lastFilled: string | null;
  team: { id: number; name: string } | null;
  fields: { id: number; name: string }[];
  chosen?: number[];
  stats: TileStat[];
  trend: { weekStart: string; value: number }[];
}
export interface CrmOption { key: string; name: string; keyLabel: string; keyHint: string; autoStats: boolean }
export const getTile = (widgetId: number) => get<TileData>(`/api/st-v2/tile/${widgetId}`);
export const saveTileFields = (widgetId: number, fieldIds: number[]) => post<{ chosen: number[] }>(`/api/st-v2/tile/${widgetId}`, { fieldIds });
export const getCrms = () => get<CrmOption[]>('/api/st-v2/crms');
export const requestCrm = (crmName: string, note: string) => post<{ ok: boolean }>('/api/st-v2/crm-request', { crmName, note });
/** Saves the key on the member's own Connections (Account > Connections), which starts the first sync. */
export const connectCrm = (provider: string, apiKey: string) => putRequest(`/api/account/connections/${provider}`, { apiKey }) as Promise<{ provider: string; status: string }>;

/** Whether Stat Tracker v2 is open for the signed-in member (asked once per page load). */
let featurePromise: Promise<boolean> | null = null;
export const statV2Open = () => {
  if (!featurePromise) featurePromise = getFeatures().then((f) => !!(f && f.statV2)).catch(() => { featurePromise = null; return false; });
  return featurePromise;
};
export const useStatV2Open = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => { let alive = true; statV2Open().then((v) => alive && setOpen(v)); return () => { alive = false; }; }, []);
  return open;
};

/* ---- team leader view + TV view (leader only, checked on the server) ---- */
export interface TeamMember { userId: number; name: string; isLeader: boolean; crm: string | null }
export interface TeamValue { userId: number; day: string; fieldId: number; value: number }
export interface TeamDay { userId: number; day: string; level: number | null; note: string | null }
export interface TeamBoard { team: V2Team; members: TeamMember[]; fields: V2Field[]; targets: V2Target[]; values: TeamValue[]; days: TeamDay[] }
export const getTeamBoard = (teamId: number, from: string, to: string) => get<TeamBoard>('/api/st-v2/team', { teamId, from, to });
export const enterFor = (teamId: number, memberId: number, fieldId: number, day: string, value: number) =>
  post<TeamValue>('/api/st-v2/enter-for', { teamId, memberId, fieldId, day, value });
